import React, { useEffect, useReducer, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Modal as AntModal } from 'antd';
import produce from 'immer';
import { TableMainText } from 'styles';

import { DEFAULT_PAGE_SIZE } from 'appConstants';
import { TableActions } from 'components';
import CustomIcon from 'components/Icons/Icon';
import { ImageItem } from 'components/Layout/list';

import BDOSettingsCardComponents from '../BDOSettingsCardComponents';
import AddEditCreditCardModal from './AddEditCreditCardModal';
import { clearCreditCardDetails, creditCardsGet, deleteCreditCard } from './actions';
import { BRAND_LABELS, CardType } from './constants';

interface ICreditCardReducerState {
  q: string | null;
  size: number;
  page: number;
}

const { confirm } = AntModal;

const CreditCardsLists = () => {
  const [isEdit, setIsEdit] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCreditCard, setSelectedCreditCard] = useState(null);

  let navigate = useNavigate();
  const dispatch = useDispatch();

  const onAddNewClick = () => {
    dispatch(clearCreditCardDetails());
    setIsEdit(false);
    setSelectedCreditCard(null);
    setModalOpen(true);
  };

  const onEditActionMenuClick = record => () => {
    setIsEdit(true);
    setSelectedCreditCard(record.id);
    setModalOpen(true);
  };

  const columns = [
    {
      title: '',
      key: 'sort',
      width: '3%',
      render: () => <CustomIcon iconName="sort" />
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => (
        <>
          <Link to={`/p/credit_card_redemption_settings/credit-cards/show/${record.id}`}>
            <TableMainText>{record.name}</TableMainText>
          </Link>
        </>
      )
    },
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      render: (_, record) => (
        <ImageItem key={record?.id}>
          <img src={record?.image?.url || record?.image?.file} alt={record?.name} />
        </ImageItem>
      )
    },
    {
      title: 'Brand',
      dataIndex: 'card_type',
      key: 'card_type',
      render: (val: string) => BRAND_LABELS[val as CardType] ?? val
    },
    {
      title: 'Product Code',
      dataIndex: 'product_code',
      key: 'product_code'
    },
    {
      title: 'BIN Number',
      dataIndex: 'bin_number',
      key: 'bin_number',
      render: (v: string | null | undefined) => v ?? '—'
    },
    {
      title: 'Min Redemption',
      dataIndex: 'minimum_points',
      key: 'minimum_points',
      render: (v: number | null | undefined) => v ?? '—'
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record) => (
        <TableActions
          testId="credit-card-action-menu"
          actions={[
            {
              title: 'View Details',
              onClick: () => {
                navigate(`/p/credit_card_redemption_settings/credit-cards/show/${record?.id}`);
              }
            },
            {
              title: 'Edit',
              onClick: onEditActionMenuClick(record)
            },
            {
              title: 'Delete',
              onClick: () =>
                confirm({
                  title: 'Do you want to delete this item?',
                  onOk() {
                    dispatch(deleteCreditCard(record?.id));
                  }
                })
            }
          ]}
        />
      ),
      align: 'right'
    }
  ];

  const paramsReducer = (state: ICreditCardReducerState, action): ICreditCardReducerState =>
    produce(state, draft => {
      switch (action.type) {
        case 'SEARCH':
          draft.q = action.value;
          draft.page = 1;
          break;
        case 'PAGINATION':
          draft.page = action.current;
          if (action.size) {
            draft.size = action.size;
          }
          break;
        default:
          break;
      }
    });

  const [params, paramsDispatch] = useReducer(paramsReducer, {
    q: null,
    size: DEFAULT_PAGE_SIZE,
    page: 1
  });

  const { creditCards, loading, error } = useSelector((state: any) => state.creditCards);

  useEffect(() => {
    dispatch(creditCardsGet(params));
  }, [params, dispatch]);

  const onCloseModal = () => {
    dispatch(clearCreditCardDetails());
    setModalOpen(false);
    setSelectedCreditCard(null);
  };

  return (
    <>
      <BDOSettingsCardComponents
        cardTitle="Credit Cards"
        loading={loading}
        columns={columns}
        data={creditCards}
        params={params}
        mutate={() => dispatch(creditCardsGet(params))}
        paramsDispatch={paramsDispatch}
        onAddNewClick={onAddNewClick}
        isCreditCard={true}
      />
      <AddEditCreditCardModal
        modalOpen={modalOpen}
        closeModal={onCloseModal}
        isEdit={isEdit}
        selectedCreditCard={isEdit ? selectedCreditCard : null}
      />
    </>
  );
};

export default CreditCardsLists;
